import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import './PhoneFrame.css';

const CYCLE_MS = 3400;
const MIN_SHIMMER_MS = 900;

// Reusable device bezel for showcasing raw app screenshots consistently.
// Pass either a single `src`/`alt`, or an `images` array (`[{ src, alt }]`) to
// auto-cycle through several screenshots with a cross-fade, shimmering in
// while the first frame loads.
export default function PhoneFrame({ src, images, alt = '', size = 'md', tilt = 0, focus = 'top', clickable = true, fadeBottom = false, className = '' }) {
  const frames = useMemo(() => (images && images.length ? images : [{ src, alt }]), [images, src, alt]);
  const [index, setIndex] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [minDelayDone, setMinDelayDone] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const style = tilt ? { '--phone-tilt': `${tilt}deg` } : undefined;
  const firstLoaded = imgLoaded && minDelayDone;

  const open = useCallback(() => clickable && setIsOpen(true), [clickable]);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  // Keeps the shimmer skeleton visible for a beat after the splash loader hands
  // off, instead of flashing past it when the screenshot loads from cache.
  useEffect(() => {
    const t = setTimeout(() => setMinDelayDone(true), MIN_SHIMMER_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (frames.length < 2) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % frames.length);
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [frames.length]);

  const current = frames[index];

  return (
    <>
      <div
        className={`phone-frame phone-frame--${size}${clickable ? ' phone-frame--clickable' : ''} ${className}`}
        style={style}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={clickable ? `View full-size: ${current.alt || alt}` : undefined}
        onClick={open}
        onKeyDown={(e) => clickable && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), open())}
      >
        <div className="phone-frame__notch" aria-hidden="true" />
        {!firstLoaded && <div className="phone-frame__shimmer" aria-hidden="true" />}
        {frames.map((frame, i) => (
          <img
            key={frame.src}
            className={`phone-frame__screen${i === index ? ' phone-frame__screen--active' : ''}`}
            src={frame.src}
            alt={i === index ? frame.alt || alt : ''}
            loading={i === 0 ? 'eager' : 'lazy'}
            style={{ objectPosition: focus }}
            onLoad={i === 0 ? () => setImgLoaded(true) : undefined}
          />
        ))}
        {fadeBottom && <div className="phone-frame__fade" aria-hidden="true" />}
      </div>

      {isOpen && createPortal(
        <div className="phone-frame__lightbox" onClick={close} role="dialog" aria-modal="true" aria-label={current.alt || alt}>
          <button type="button" className="phone-frame__lightbox-close" onClick={close} aria-label="Close full-size image">
            &times;
          </button>
          <img
            className="phone-frame__lightbox-img"
            src={current.src}
            alt={current.alt || alt}
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body,
      )}
    </>
  );
}

